// Phase 6 — Track D T-D6. OfflineBanner.
//
// Renders nothing while online. When the browser goes offline, a small
// surface-overlay strip appears above the NavBar with an aria-live
// announcement and a dismiss control. Dismissal hides the banner for the
// duration of the current offline session; the next online -> offline
// transition resets the dismiss flag so the banner reappears.
//
// The shared copy bundle does not yet expose a dedicated offline_banner
// string (Phase 6 plan B-B5 lists only the existing keys); per blueprint
// section 13.4 the closest user-facing copy is `network_unavailable`,
// which speaks the same intent ("you are offline; reconnect"). Track G's
// final copy review can swap this to a dedicated key without touching
// the structural code here.

import * as React from 'react';
import { X } from 'lucide-react';
import { copy } from '@dailydeck/shared/copy';
import { IconButton } from './ui/IconButton.js';
import { useOnline } from '../hooks/useOnline.js';
import { cn } from '../lib/cn.js';

export function OfflineBanner(): JSX.Element | null {
  const online = useOnline();
  const [dismissed, setDismissed] = React.useState(false);

  // When connectivity is restored, clear the dismiss flag so the next
  // offline transition shows the banner again.
  React.useEffect(() => {
    if (online) {
      setDismissed(false);
    }
  }, [online]);

  if (online || dismissed) {
    return null;
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'sticky top-0 z-50 flex items-center justify-between gap-3 px-4 py-2',
        'bg-surface-overlay border-b border-border-default text-text-primary text-caption',
      )}
      data-testid="offline-banner"
    >
      <span>{copy.errors.network_unavailable}</span>
      <IconButton
        aria-label="Dismiss offline notice"
        onClick={() => setDismissed(true)}
        className="h-7 w-7"
      >
        <X size={14} strokeWidth={1.5} aria-hidden="true" />
      </IconButton>
    </div>
  );
}
